import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowUpRight,
  Loader2,
  Sparkles,
  Package,
  Plus,
  Trash2,
  Rocket,
  Search,
} from "lucide-react";

const EXPANSION_LABELS = {
  add_panels:        "Add panels",
  add_battery:       "Add battery",
  add_ev:            "Add EV charger",
  upgrade_inverter:  "Upgrade inverter",
  add_monitoring:    "Add monitoring",
  other:             "Other",
};

const STATUSES = [
  { key: "all",       label: "All" },
  { key: "new",       label: "New" },
  { key: "reviewing", label: "Reviewing" },
  { key: "quoted",    label: "Quoted" },
  { key: "converted", label: "Converted" },
  { key: "declined",  label: "Declined" },
];

const STATUS_STYLES = {
  new:        "bg-amber-100 text-amber-700",
  reviewing:  "bg-sky-100 text-sky-700",
  quoted:     "bg-emerald-100 text-emerald-700",
  converted:  "bg-emerald-600 text-white",
  declined:   "bg-slate-200 text-slate-600",
};

export default function AdminExpansions() {
  const [rows, setRows] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [busyId, setBusyId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [materials, setMaterials] = useState([]);

  const load = async () => {
    const r = await api.get(`/expansion-requests${statusFilter === "all" ? "" : `?status=${statusFilter}`}`);
    setRows(r.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  useEffect(() => {
    api.get("/materials").then((r) => setMaterials(r.data || [])).catch(() => {});
  }, []);

  const setStatus = async (r, status) => {
    setBusyId(r.request_id);
    try {
      await api.patch(`/expansion-requests/${r.request_id}`, { status });
      toast.success(`Marked ${STATUSES.find((s) => s.key === status)?.label || status}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Something went wrong — please try again.");
    } finally { setBusyId(null); }
  };

  const saveNote = async (r, note) => {
    setBusyId(r.request_id);
    try {
      await api.patch(`/expansion-requests/${r.request_id}`, { admin_notes: note });
      toast.success("Note saved");
      await load();
    } catch (e) {
      toast.error("Save failed");
    } finally { setBusyId(null); }
  };

  const addMaterial = async (r, material_id, qty) => {
    try {
      await api.post(`/expansion-requests/${r.request_id}/draft-materials`, { material_id, qty });
      toast.success("Material added");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Something went wrong — please try again.");
    }
  };

  const removeMaterial = async (r, line_id) => {
    try {
      await api.delete(`/expansion-requests/${r.request_id}/draft-materials/${line_id}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Something went wrong — please try again.");
    }
  };

  const convert = async (r) => {
    if (!(r.draft_materials || []).length) {
      toast.error("Add at least one material first");
      return;
    }
    if (!window.confirm(`Create a new expansion project for ${r.user_name} and reserve ${(r.draft_materials || []).length} material line(s)?`)) return;
    setBusyId(r.request_id);
    try {
      const resp = await api.post(`/expansion-requests/${r.request_id}/convert`);
      toast.success(`Expansion project ${resp.data.project?.project_number || ""} created`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Convert failed");
    } finally { setBusyId(null); }
  };

  const counts = useMemo(() => {
    const c = { new: 0, reviewing: 0, quoted: 0 };
    for (const r of rows) if (r.status in c) c[r.status] += 1;
    return c;
  }, [rows]);

  return (
    <div data-testid="admin-expansions">
      <p className="overline">Client requests</p>
      <h1 className="text-3xl sm:text-4xl font-heading font-medium text-[#0A2540] dark:text-white mt-2 mb-2">
        Expansion requests
      </h1>
      <p className="text-sm text-[#475569] dark:text-slate-400 mb-6">
        Upgrade requests from clients with an installed PowerBuilt system. Draft the materials
        for the quote inline, then convert directly into a new expansion project.
      </p>

      {/* Filter tabs */}
      <div className="flex flex-wrap gap-2 mb-4">
        {STATUSES.map((s) => (
          <button
            key={s.key}
            onClick={() => setStatusFilter(s.key)}
            data-testid={`exp-tab-${s.key}`}
            className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-full text-xs font-semibold transition ${
              statusFilter === s.key
                ? "bg-[#0A2540] text-white"
                : "bg-white dark:bg-slate-900 border border-[#E2E8F0] dark:border-slate-700 text-[#475569] dark:text-slate-300 hover:border-[#0A2540]"
            }`}
          >
            {s.label}
            {s.key !== "all" && counts[s.key] > 0 && (
              <span className={`text-[10px] rounded-full px-1.5 ${statusFilter === s.key ? "bg-white/20" : "bg-slate-100 dark:bg-slate-800"}`}>
                {counts[s.key]}
              </span>
            )}
          </button>
        ))}
      </div>

      {rows.length === 0 ? (
        <div className="card-pb p-10 text-center">
          <Sparkles className="w-8 h-8 text-[#E2E8F0] mx-auto mb-2" />
          <p className="text-sm text-[#475569] dark:text-slate-400">
            No expansion requests {statusFilter !== "all" && `in status "${statusFilter}"`} yet.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => {
            const isOpen = expandedId === r.request_id;
            const draftCount = (r.draft_materials || []).length;
            const draftTotal = (r.draft_materials || []).reduce(
              (s, l) => s + (l.qty || 0) * (l.unit_cost || 0), 0,
            );
            return (
              <article
                key={r.request_id}
                data-testid={`exp-row-${r.request_id}`}
                className="card-pb overflow-hidden"
              >
                <button
                  onClick={() => setExpandedId(isOpen ? null : r.request_id)}
                  className="w-full text-left p-4 flex flex-wrap items-center gap-3"
                >
                  <div className="w-10 h-10 rounded-lg bg-[#C9A356]/15 grid place-items-center text-[#C9A356] shrink-0">
                    <ArrowUpRight className="w-5 h-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-[#0A2540] dark:text-white truncate">{r.user_name || "Unknown"}</span>
                      <span className="font-mono text-xs text-[#C9A356]">{r.customer_code || "—"}</span>
                      <span className={`text-[9px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full ${STATUS_STYLES[r.status] || "bg-slate-100"}`}>
                        {r.status}
                      </span>
                      {draftCount > 0 && (
                        <span className="inline-flex items-center gap-1 text-[9px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">
                          <Package className="w-3 h-3" /> {draftCount} line{draftCount !== 1 ? "s" : ""}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-[#475569] dark:text-slate-400 mt-0.5">
                      {r.project_title || "System"}
                      {r.project_kw ? ` · ${r.project_kw} kW` : ""}
                      {" · "}
                      {r.types.map((t) => EXPANSION_LABELS[t] || t).join(", ")}
                    </div>
                  </div>
                  <div className="text-right text-xs text-[#94A3B8] shrink-0">
                    {new Date(r.created_at).toLocaleDateString()}
                    {draftTotal > 0 ? (
                      <div className="text-[#0A2540] dark:text-white font-semibold">${draftTotal.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
                    ) : r.budget ? (
                      <div className="text-[#0A2540] dark:text-white font-medium">~ ${r.budget.toLocaleString()}</div>
                    ) : null}
                  </div>
                </button>

                {isOpen && (
                  <div className="border-t border-[#E2E8F0] dark:border-slate-700 p-4 space-y-4 bg-slate-50/60 dark:bg-slate-900/30">
                    {r.notes && (
                      <div>
                        <p className="overline mb-1">Client notes</p>
                        <p className="text-sm text-[#0A2540] dark:text-white whitespace-pre-wrap">{r.notes}</p>
                      </div>
                    )}

                    <div>
                      <p className="overline mb-1">Admin notes</p>
                      <AdminNoteBox request={r} onSave={saveNote} busy={busyId === r.request_id} />
                    </div>

                    {/* Materials draft — the picker */}
                    <DraftMaterialsCart
                      request={r}
                      materials={materials}
                      onAdd={addMaterial}
                      onRemove={removeMaterial}
                      onConvert={convert}
                      converting={busyId === r.request_id}
                    />

                    <div className="flex flex-wrap gap-2 border-t border-[#E2E8F0] dark:border-slate-700 pt-4">
                      {["reviewing", "quoted", "declined"].map((s) => (
                        <button
                          key={s}
                          onClick={() => setStatus(r, s)}
                          disabled={r.status === s || busyId === r.request_id}
                          data-testid={`exp-set-${s}-${r.request_id}`}
                          className={`h-9 px-3 rounded-md text-xs font-semibold transition ${
                            r.status === s
                              ? "bg-slate-200 text-slate-500 cursor-not-allowed"
                              : "bg-[#0A2540] text-white hover:bg-[#12365b]"
                          }`}
                        >
                          Mark {STATUSES.find((x) => x.key === s)?.label}
                        </button>
                      ))}
                      {r.converted_project_id && (
                        <a
                          href={`/admin/projects/${r.converted_project_id}`}
                          className="h-9 px-3 rounded-md text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white inline-flex items-center gap-1"
                          data-testid={`exp-open-project-${r.request_id}`}
                        >
                          <Rocket className="w-3.5 h-3.5" /> Open expansion project
                        </a>
                      )}
                    </div>
                  </div>
                )}
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}

function AdminNoteBox({ request, onSave, busy }) {
  const [note, setNote] = useState(request.admin_notes || "");
  useEffect(() => { setNote(request.admin_notes || ""); }, [request.request_id, request.admin_notes]);
  return (
    <div>
      <textarea
        value={note}
        onChange={(e) => setNote(e.target.value)}
        rows={2}
        maxLength={2000}
        placeholder="Private admin note — client can't see this."
        data-testid={`admin-note-${request.request_id}`}
        className="w-full rounded-md border border-[#E2E8F0] dark:border-slate-700 dark:bg-slate-800 dark:text-white px-3 py-2 text-sm"
      />
      <button
        onClick={() => onSave(request, note)}
        disabled={busy || note === (request.admin_notes || "")}
        data-testid={`admin-note-save-${request.request_id}`}
        className="mt-2 h-8 px-3 rounded-md bg-[#C9A356] hover:bg-[#DDBD77] text-white text-xs font-semibold disabled:opacity-50"
      >
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Save note"}
      </button>
    </div>
  );
}

/* ---------- Draft materials cart (inline picker) ---------- */

const CATS = [
  { key: "all",      label: "All" },
  { key: "panel",    label: "Panels" },
  { key: "inverter", label: "Inverters" },
  { key: "battery",  label: "Batteries" },
  { key: "wiring",   label: "Wiring" },
  { key: "mounting", label: "Mounting" },
  { key: "general",  label: "General" },
];

function DraftMaterialsCart({ request, materials, onAdd, onRemove, onConvert, converting }) {
  const [category, setCategory] = useState("all");
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState(null);
  const [qty, setQty] = useState(1);
  const [adding, setAdding] = useState(false);
  const canEdit = request.status !== "converted";

  const draft = request.draft_materials || [];
  const total = draft.reduce((s, l) => s + (l.qty || 0) * (l.unit_cost || 0), 0);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return (materials || []).filter((m) => {
      if (category !== "all" && (m.category || "general") !== category) return false;
      if (query) {
        const hay = `${m.name || ""} ${m.sku || ""} ${m.vendor || ""}`.toLowerCase();
        if (!hay.includes(query)) return false;
      }
      return true;
    });
  }, [materials, category, q]);

  const available = picked ? Number(picked.stock || 0) - Number(picked.reserved || 0) : 0;

  const submit = async () => {
    if (!picked || qty <= 0) return;
    setAdding(true);
    try {
      await onAdd(request, picked.material_id, parseInt(qty, 10));
      setPicked(null);
      setQty(1);
    } finally { setAdding(false); }
  };

  return (
    <div data-testid={`draft-cart-${request.request_id}`}>
      <div className="flex items-center justify-between mb-2">
        <p className="overline mb-0 inline-flex items-center gap-1.5">
          <Package className="w-3 h-3 text-[#C9A356]" /> Draft materials for quote
        </p>
        {draft.length > 0 && (
          <span className="text-xs text-[#475569] dark:text-slate-400">
            {draft.length} line · <b className="text-[#0A2540] dark:text-white">${total.toLocaleString(undefined, { maximumFractionDigits: 2 })}</b>
          </span>
        )}
      </div>

      {/* Current draft lines */}
      {draft.length === 0 ? (
        <p className="text-xs text-[#94A3B8] italic mb-3">No materials picked yet — quote can be drafted here without leaving the queue.</p>
      ) : (
        <ul className="rounded-lg border border-[#E2E8F0] dark:border-slate-700 divide-y divide-[#E2E8F0] dark:divide-slate-700 mb-3 bg-white dark:bg-slate-900">
          {draft.map((l) => (
            <li key={l.line_id} className="px-3 py-2 flex items-center gap-3">
              <div className="min-w-0 flex-1">
                <div className="text-sm text-[#0A2540] dark:text-white truncate">{l.material_name}</div>
                <div className="text-[10px] text-[#94A3B8] font-mono">{l.sku || "—"} · {l.category}</div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-sm tabular-nums text-[#0A2540] dark:text-white">{l.qty} × ${(l.unit_cost || 0).toFixed(2)}</div>
                <div className="text-[10px] text-[#94A3B8] tabular-nums">${((l.qty || 0) * (l.unit_cost || 0)).toFixed(2)}</div>
              </div>
              {canEdit && (
                <button
                  onClick={() => onRemove(request, l.line_id)}
                  className="w-8 h-8 grid place-items-center text-red-500 hover:bg-red-50 rounded-md"
                  aria-label="Remove"
                  data-testid={`draft-remove-${l.line_id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {canEdit && (
        <div className="rounded-lg border border-dashed border-[#E2E8F0] dark:border-slate-700 p-3 space-y-3 bg-white/60 dark:bg-slate-900/40">
          <div className="flex flex-wrap gap-1.5">
            {CATS.map((c) => (
              <button
                key={c.key}
                onClick={() => { setCategory(c.key); setPicked(null); }}
                data-testid={`draft-cat-${request.request_id}-${c.key}`}
                className={`h-8 px-2.5 rounded-full text-[11px] font-semibold transition ${
                  category === c.key
                    ? "bg-[#0A2540] text-white"
                    : "bg-white dark:bg-slate-900 border border-[#E2E8F0] dark:border-slate-700 text-[#475569] dark:text-slate-300 hover:border-[#0A2540]"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>

          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search materials by name, SKU or vendor"
              data-testid={`draft-search-${request.request_id}`}
              className="w-full h-9 rounded-md border border-[#E2E8F0] dark:border-slate-700 dark:bg-slate-800 dark:text-white pl-9 pr-3 text-xs"
            />
          </div>

          <div className="rounded border border-[#E2E8F0] dark:border-slate-700 divide-y divide-[#E2E8F0] dark:divide-slate-800 max-h-52 overflow-y-auto bg-white dark:bg-slate-900">
            {filtered.length === 0 ? (
              <p className="p-3 text-xs text-[#94A3B8] text-center italic">No materials match.</p>
            ) : (
              filtered.slice(0, 40).map((m) => {
                const avail = Number(m.stock || 0) - Number(m.reserved || 0);
                const isPicked = picked?.material_id === m.material_id;
                const out = avail <= 0;
                return (
                  <button
                    key={m.material_id}
                    onClick={() => { if (!out) { setPicked(m); setQty(1); } }}
                    disabled={out}
                    data-testid={`draft-pick-${request.request_id}-${m.material_id}`}
                    className={`w-full text-left px-3 py-2 flex items-center justify-between gap-3 text-xs ${
                      isPicked ? "bg-[#C9A356]/10" : "hover:bg-slate-50 dark:hover:bg-slate-800/60"
                    } ${out ? "opacity-40 cursor-not-allowed" : ""}`}
                  >
                    <div className="min-w-0">
                      <div className="text-[#0A2540] dark:text-white truncate">{m.name}</div>
                      <div className="text-[10px] text-[#94A3B8] font-mono truncate">
                        {m.sku || "—"} · ${(Number(m.cost_usd) || 0).toFixed(2)}/{m.unit || "ea"}
                      </div>
                    </div>
                    <div className={`text-[10px] font-semibold tabular-nums shrink-0 ${
                      out ? "text-red-500" : avail <= (m.reorder_level || 0) ? "text-amber-600" : "text-emerald-600"
                    }`}>
                      {avail} avail
                    </div>
                  </button>
                );
              })
            )}
          </div>

          {picked && (
            <div className="rounded-md border-2 border-[#C9A356] bg-[#C9A356]/5 p-3 flex items-center gap-3 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-wider text-[#C9A356] font-bold">Selected</div>
                <div className="text-sm text-[#0A2540] dark:text-white truncate">{picked.name}</div>
                <div className="text-[10px] text-[#94A3B8]">Available: {available}</div>
              </div>
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => setQty((q) => Math.max(1, (parseInt(q, 10) || 1) - 1))}
                  className="w-8 h-8 rounded-md bg-slate-100 dark:bg-slate-800 text-[#0A2540] dark:text-white"
                >−</button>
                <input
                  type="number" min="1"
                  value={qty}
                  onChange={(e) => setQty(e.target.value)}
                  data-testid={`draft-qty-${request.request_id}`}
                  className="w-16 h-8 rounded-md border border-[#E2E8F0] dark:border-slate-700 dark:bg-slate-800 dark:text-white px-1 text-center text-xs tabular-nums"
                />
                <button
                  type="button"
                  onClick={() => setQty((q) => (parseInt(q, 10) || 0) + 1)}
                  className="w-8 h-8 rounded-md bg-slate-100 dark:bg-slate-800 text-[#0A2540] dark:text-white"
                >+</button>
              </div>
              <button
                onClick={submit}
                disabled={adding}
                data-testid={`draft-add-${request.request_id}`}
                className="h-9 px-3 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold disabled:opacity-50 inline-flex items-center gap-1"
              >
                <Plus className="w-3.5 h-3.5" /> {adding ? "Adding…" : "Add to draft"}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Convert action */}
      {canEdit && draft.length > 0 && (
        <button
          onClick={() => onConvert(request)}
          disabled={converting}
          data-testid={`exp-convert-${request.request_id}`}
          className="mt-3 h-10 px-4 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50"
        >
          <Rocket className="w-4 h-4" />
          {converting ? "Creating…" : `Create expansion project (${draft.length} lines · $${total.toLocaleString(undefined, { maximumFractionDigits: 0 })})`}
        </button>
      )}
    </div>
  );
}
